import { useEffect, useRef, useState } from 'react';

export default function Testimonials() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const animationRef = useRef<number>();
  const lastTimeRef = useRef<number>(0);
  const scrollPositionRef = useRef(0);

  const testimonials = [
    {
      image: 'https://public.readdy.ai/ai/img_res/0270466ecc39fa4981d21d5ee1781c50.jpg',
      quote: 'GI2 has been our reliable supplier for 3 years. Their bulk pricing and consistent stock availability have helped us grow our retail business significantly.',
      name: 'Rajesh Kumar',
      position: 'Retailer, Mumbai',
      category: 'Toner Cartridges',
      rating: 5,
    },
    {
      image: 'https://readdy.ai/api/search-image?query=professional%20Indian%20female%20business%20owner%20portrait%20in%20wholesale%20warehouse%20environment%20confident%20expression%20natural%20lighting%20realistic%20photography&width=200&height=200&seq=test2&orientation=squarish',
      quote: 'As a wholesaler, timely delivery is crucial. GI2 never disappoints. Their lamination products are top quality and competitively priced for bulk orders.',
      name: 'Priya Sharma',
      position: 'Wholesaler, Delhi',
      category: 'Lamination Products',
      rating: 5,
    },
    {
      image: 'https://readdy.ai/api/search-image?query=professional%20Indian%20male%20technician%20portrait%20in%20printer%20service%20center%20environment%20confident%20expression%20natural%20lighting%20realistic%20photography&width=200&height=200&seq=test3&orientation=squarish',
      quote: 'Running a service center requires quality OPC drums and toner powder. GI2 provides genuine products at wholesale rates with quick dispatch across India.',
      name: 'Amit Patel',
      position: 'Service Center, Bangalore',
      category: 'OPC Drums',
      rating: 5,
    },
    {
      image: 'https://readdy.ai/api/search-image?query=professional%20Indian%20female%20entrepreneur%20portrait%20in%20modern%20office%20environment%20confident%20expression%20natural%20lighting%20realistic%20photography&width=200&height=200&seq=test4&orientation=squarish',
      quote: 'The quality of their compatible cartridges is outstanding. We have been ordering in bulk for our chain of stores and the consistency is remarkable.',
      name: 'Sneha Reddy',
      position: 'Chain Store Owner, Hyderabad',
      category: 'Compatible Cartridges',
      rating: 5,
    },
    {
      image: 'https://readdy.ai/api/search-image?query=professional%20Indian%20male%20distributor%20portrait%20in%20warehouse%20environment%20confident%20expression%20natural%20lighting%20realistic%20photography&width=200&height=200&seq=test5&orientation=squarish',
      quote: 'Best B2B partner for printer consumables. Their credit terms and bulk discounts have improved our profit margins significantly. Highly recommended!',
      name: 'Vikram Singh',
      position: 'Distributor, Jaipur',
      category: 'Bulk Orders',
      rating: 5,
    },
    {
      image: 'https://readdy.ai/api/search-image?query=professional%20Indian%20female%20shop%20owner%20portrait%20in%20retail%20store%20environment%20confident%20expression%20natural%20lighting%20realistic%20photography&width=200&height=200&seq=test6&orientation=squarish',
      quote: 'Their customer support is exceptional. Any query is resolved within hours. The product range is extensive and pricing is very competitive for retailers.',
      name: 'Kavita Desai',
      position: 'Retailer, Pune',
      category: 'Customer Support',
      rating: 5,
    },
    {
      image: 'https://readdy.ai/api/search-image?query=professional%20Indian%20male%20business%20partner%20portrait%20in%20office%20environment%20confident%20expression%20natural%20lighting%20realistic%20photography&width=200&height=200&seq=test7&orientation=squarish',
      quote: 'We switched to GI2 last year and it was the best decision. Their lamination sheets quality is superior and delivery is always on time across all our branches.',
      name: 'Arjun Mehta',
      position: 'Wholesaler, Ahmedabad',
      category: 'Lamination Sheets',
      rating: 5,
    },
    {
      image: 'https://readdy.ai/api/search-image?query=professional%20Indian%20female%20service%20center%20owner%20portrait%20in%20technical%20workshop%20environment%20confident%20expression%20natural%20lighting%20realistic%20photography&width=200&height=200&seq=test8&orientation=squarish',
      quote: 'Genuine products at wholesale prices with fast shipping. GI2 understands the needs of service centers and always keeps the essential items in stock.',
      name: 'Meera Iyer',
      position: 'Service Center, Chennai',
      category: 'Service Parts',
      rating: 5,
    },
  ];

  // Desktop smooth infinite scroll animation with delta time
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || window.innerWidth < 1024) return;

    const pixelsPerSecond = 30; // Smooth constant speed

    const scroll = (currentTime: number) => {
      if (!lastTimeRef.current) {
        lastTimeRef.current = currentTime;
      }

      const deltaTime = (currentTime - lastTimeRef.current) / 1000; // Convert to seconds
      lastTimeRef.current = currentTime;

      if (!isPaused && scrollContainer) {
        scrollPositionRef.current += pixelsPerSecond * deltaTime;
        
        const cardWidth = 396; // Card width (380px) + gap (16px)
        const totalWidth = cardWidth * testimonials.length;
        
        // Seamless loop reset
        if (scrollPositionRef.current >= totalWidth) {
          scrollPositionRef.current -= totalWidth;
        }
        
        scrollContainer.scrollLeft = scrollPositionRef.current;
      }
      
      animationRef.current = requestAnimationFrame(scroll);
    };

    animationRef.current = requestAnimationFrame(scroll);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      lastTimeRef.current = 0;
    };
  }, [isPaused, testimonials.length]);

  // Mobile infinite scroll detection
  useEffect(() => {
    const mobileContainer = mobileScrollRef.current;
    if (!mobileContainer || window.innerWidth >= 1024) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = mobileContainer;
      const cardWidth = 296; // Card width (280px) + gap (16px)
      const totalWidth = cardWidth * testimonials.length;

      // When scrolled near the end, reset to beginning
      if (scrollLeft + clientWidth >= scrollWidth - 50) {
        mobileContainer.scrollLeft = scrollLeft - totalWidth;
      }
      
      // When scrolled near the beginning (backwards), jump to end
      if (scrollLeft <= 50) {
        mobileContainer.scrollLeft = scrollLeft + totalWidth;
      }
    };

    mobileContainer.addEventListener('scroll', handleScroll);
    
    // Set initial scroll position to middle for seamless loop
    mobileContainer.scrollLeft = 296 * testimonials.length;

    return () => {
      mobileContainer.removeEventListener('scroll', handleScroll);
    };
  }, [testimonials.length]);

  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  return (
    <section className="py-16 lg:py-20 bg-gradient-to-br from-gray-50 via-white to-teal-50 overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full mb-4">
            <i className="ri-star-fill text-orange-500 text-lg"></i>
            <span className="text-sm font-bold text-teal-900">Client Success Stories</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-black text-gray-900 mb-4">
            Trusted by Trade Buyers
          </h2>
          <p className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">
            Join <strong className="text-teal-600">500+ satisfied partners</strong> across India who trust us for their printer consumable needs
          </p>
        </div>

        {/* Desktop View - Smooth Infinite Horizontal Scroll */}
        <div className="hidden lg:block relative">
          <div 
            ref={scrollRef}
            className="flex gap-4 overflow-x-hidden"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {/* Original cards */}
            {testimonials.map((testimonial, index) => (
              <div
                key={`original-${index}`}
                className="flex-shrink-0 w-[380px]"
              >
                <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border border-gray-100 group">
                  {/* Verified Badge - Moved to top right */}
                  <div className="absolute top-4 right-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-600 text-white text-xs font-bold rounded-full shadow-lg">
                      <i className="ri-verified-badge-fill"></i>
                      Verified
                    </span>
                  </div>

                  {/* Rating Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-orange-400 text-lg"></i>
                    ))}
                  </div>

                  {/* Quote */}
                  <div className="mb-6">
                    <i className="ri-double-quotes-l text-4xl text-teal-500 opacity-20 absolute top-16 right-6"></i>
                    <p className="text-gray-700 text-base leading-relaxed relative z-10">
                      {testimonial.quote}
                    </p>
                  </div>

                  {/* Profile Section */}
                  <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-teal-500 flex-shrink-0">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-bold text-base truncate">{testimonial.name}</p>
                      <p className="text-gray-500 text-sm truncate">{testimonial.position}</p>
                    </div>
                  </div>

                  {/* Category Badge */}
                  <div className="mt-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full">
                      <i className="ri-shopping-bag-3-line"></i>
                      {testimonial.category}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Duplicated cards for seamless loop */}
            {testimonials.map((testimonial, index) => (
              <div
                key={`duplicate-${index}`}
                className="flex-shrink-0 w-[380px]"
              >
                <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border border-gray-100 group">
                  {/* Verified Badge - Moved to top right */}
                  <div className="absolute top-4 right-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-600 text-white text-xs font-bold rounded-full shadow-lg">
                      <i className="ri-verified-badge-fill"></i>
                      Verified
                    </span>
                  </div>

                  {/* Rating Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-orange-400 text-lg"></i>
                    ))}
                  </div>

                  {/* Quote */}
                  <div className="mb-6">
                    <i className="ri-double-quotes-l text-4xl text-teal-500 opacity-20 absolute top-16 right-6"></i>
                    <p className="text-gray-700 text-base leading-relaxed relative z-10">
                      {testimonial.quote}
                    </p>
                  </div>

                  {/* Profile Section */}
                  <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-teal-500 flex-shrink-0">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-bold text-base truncate">{testimonial.name}</p>
                      <p className="text-gray-500 text-sm truncate">{testimonial.position}</p>
                    </div>
                  </div>

                  {/* Category Badge */}
                  <div className="mt-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full">
                      <i className="ri-shopping-bag-3-line"></i>
                      {testimonial.category}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pause Indicator */}
          {isPaused && (
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg">
              <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                <i className="ri-pause-circle-line text-teal-600"></i>
                Paused
              </span>
            </div>
          )}
        </div>

        {/* Mobile View - Infinite Scroll Carousel */}
        <div className="lg:hidden">
          <div 
            ref={mobileScrollRef}
            className="overflow-x-auto pb-4 -mx-6 px-6 scrollbar-hide snap-x snap-mandatory"
            style={{ scrollBehavior: 'smooth' }}
          >
            <div className="flex gap-4 w-max">
              {/* First duplicate set for seamless backward scroll */}
              {testimonials.map((testimonial, index) => (
                <div
                  key={`pre-${index}`}
                  className="w-[280px] flex-shrink-0 snap-start"
                >
                  <div className="relative bg-white rounded-xl p-5 shadow-md border border-gray-100 h-full flex flex-col">
                    {/* Header with Profile */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-teal-500 flex-shrink-0">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 font-bold text-sm truncate">{testimonial.name}</p>
                        <p className="text-gray-500 text-xs truncate mb-1">{testimonial.position}</p>
                        <div className="flex gap-0.5">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-orange-400 text-xs"></i>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Quote */}
                    <p className="text-gray-700 text-sm leading-relaxed mb-4 flex-1 line-clamp-4">
                      {testimonial.quote}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs text-teal-600 font-medium flex items-center gap-1">
                        <i className="ri-shopping-bag-3-line"></i>
                        {testimonial.category}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full">
                        <i className="ri-verified-badge-fill"></i>
                        Verified
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Original set */}
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="w-[280px] flex-shrink-0 snap-start"
                >
                  <div className="relative bg-white rounded-xl p-5 shadow-md border border-gray-100 h-full flex flex-col">
                    {/* Header with Profile */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-teal-500 flex-shrink-0">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 font-bold text-sm truncate">{testimonial.name}</p>
                        <p className="text-gray-500 text-xs truncate mb-1">{testimonial.position}</p>
                        <div className="flex gap-0.5">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-orange-400 text-xs"></i>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Quote */}
                    <p className="text-gray-700 text-sm leading-relaxed mb-4 flex-1 line-clamp-4">
                      {testimonial.quote}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs text-teal-600 font-medium flex items-center gap-1">
                        <i className="ri-shopping-bag-3-line"></i>
                        {testimonial.category}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full">
                        <i className="ri-verified-badge-fill"></i>
                        Verified
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Second duplicate set for seamless forward scroll */}
              {testimonials.map((testimonial, index) => (
                <div
                  key={`post-${index}`}
                  className="w-[280px] flex-shrink-0 snap-start"
                >
                  <div className="relative bg-white rounded-xl p-5 shadow-md border border-gray-100 h-full flex flex-col">
                    {/* Header with Profile */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-teal-500 flex-shrink-0">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 font-bold text-sm truncate">{testimonial.name}</p>
                        <p className="text-gray-500 text-xs truncate mb-1">{testimonial.position}</p>
                        <div className="flex gap-0.5">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-orange-400 text-xs"></i>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Quote */}
                    <p className="text-gray-700 text-sm leading-relaxed mb-4 flex-1 line-clamp-4">
                      {testimonial.quote}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs text-teal-600 font-medium flex items-center gap-1">
                        <i className="ri-shopping-bag-3-line"></i>
                        {testimonial.category}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full">
                        <i className="ri-verified-badge-fill"></i>
                        Verified
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Scroll Hint */}
          <div className="text-center mt-4">
            <p className="text-xs text-gray-500 flex items-center justify-center gap-2">
              <i className="ri-arrow-left-right-line"></i>
              Swipe to see more reviews
            </p>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 lg:mt-12 grid grid-cols-3 lg:flex lg:flex-wrap items-center justify-center gap-3 lg:gap-12">
          <div className="flex items-center gap-3 lg:flex-row flex-col lg:w-auto lg:gap-3 gap-1">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-star-fill text-xl lg:text-2xl text-orange-500"></i>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-lg lg:text-2xl font-black text-gray-900 whitespace-nowrap">4.9/5</p>
              <p className="text-[10px] lg:text-xs text-gray-600 leading-tight">Average Rating</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 lg:flex-row flex-col lg:w-auto lg:gap-3 gap-1">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-team-fill text-xl lg:text-2xl text-orange-600"></i>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-lg lg:text-2xl font-black text-gray-900 whitespace-nowrap">500+</p>
              <p className="text-[10px] lg:text-xs text-gray-600 leading-tight">Happy Partners</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 lg:flex-row flex-col lg:w-auto lg:gap-3 gap-1">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-map-pin-fill text-xl lg:text-2xl text-teal-600"></i>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-lg lg:text-2xl font-black text-gray-900 whitespace-nowrap">28</p>
              <p className="text-[10px] lg:text-xs text-gray-600 leading-tight">States Covered</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}
